import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import {
  categoryMaxValues,
  fixedValue,
  flexValue,
  addDebtValue,
} from "../domain/entities/maxValues";
import type { FinancialStatementTotals } from "../domain/entities/financial";
import { InputRow } from "./InputRow";
import SummaryTotals from "./SummaryTotals";
import { selectedStyle } from "./common";
import {Allowed} from "./MoneyFrequencyInput";

export type CategoryItemMeta = {
  key: string;
  title: string;
  category: string;
};

export type ParentTitle =
  | "Income"
  | "Fixed Expenditure"
  | "Flexible Expenditure"
  | "Additional Debt";

interface CategoryInputsPanelProps {
  items: CategoryItemMeta[];
  namePrefix: string;
  totals: FinancialStatementTotals;
  parentTitle: ParentTitle;
  renderExtra?: (key: string) => React.ReactNode;
  renderUnderSummary?: React.ReactNode;
  extraCategories?: string[];
  renderForCategory?: (category: string) => React.ReactNode;
}

export function CategoryInputsPanel({
  items,
  namePrefix,
  totals,
  parentTitle,
  renderExtra,
  renderUnderSummary,
  extraCategories = [],
  renderForCategory,
}: Readonly<CategoryInputsPanelProps>) {
  const categories = useMemo(
    () =>
      Array.from(
        new Set([...items.map((i) => i.category), ...(extraCategories ?? [])]),
      ).filter(Boolean),
    [items, extraCategories],
  );

  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    if (!activeCategory && categories.length > 0) {
      setActiveCategory(categories[0]);
    }
  }, [activeCategory, categories]);

  const filtered = useMemo(
    () => items.filter((i) => i.category === activeCategory),
    [items, activeCategory],
  );

  const stepTotals = useMemo(
    () => totals.categories.filter((c) => c.parent === parentTitle),
    [totals, parentTitle],
  );

  const getMaxForCategory = useCallback(
    (category: string): number | undefined => {
      switch (parentTitle) {
        case "Income":
          return categoryMaxValues[category] ?? undefined;
        case "Fixed Expenditure":
          return fixedValue[category] ?? undefined;
        case "Flexible Expenditure":
          return flexValue[category] ?? undefined;
        case "Additional Debt":
          return addDebtValue[category] ?? undefined;
        default:
          return undefined;
      }
    },
    [parentTitle],
  );

  return (
    <Grid container spacing={4} alignItems="flex-start">
      <Grid item xs={12} md={6}>
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {categories.map((c) => (
            <Button
              key={c}
              variant="outlined"
              sx={{
                marginRight: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
                marginBottom: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
                textTransform: "none",
                borderRadius: 0,
                backgroundColor: "primary.light",
                fontSize: { xs: 17, sm: 17, md: 18, lg: 19, xl: 16 },
                ...selectedStyle(c === activeCategory),
              }}
              onClick={() => setActiveCategory(c)}
            >
              {c}
            </Button>
          ))}
        </Box>

        <Grid container spacing={2}>
          {filtered.map((m) => (
            <Grid item key={m.key} xs={12}>
              <InputRow
                name={`${namePrefix}.${m.key}` as Allowed}
                label={m.title}
                parentTitle={parentTitle}
                maxValue={getMaxForCategory(m.category)}
              />
              {renderExtra?.(m.key) ?? null}
            </Grid>
          ))}
        </Grid>
        {renderForCategory && (
          <Box mt={2} width="100%">
            {renderForCategory(activeCategory ?? "")}
          </Box>
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <SummaryTotals
          title={`Monthly ${parentTitle} Summary`}
          stepTotals={stepTotals}
        />
        {renderUnderSummary && (
          <Box mt={2} width="100%">
            {renderUnderSummary}
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
